import { useAppSelector } from "@/hooks/redux";
import * as Sentry from "@sentry/react";
import log from "loglevel";
import { useEffect } from "react";
import { useAuthentication } from "../auth";
import { sentryDSN } from "../config";
import { selectSessionState } from "../features/sessionSlice";
import { redactGatewaySecrets } from "./redactGatewaySecrets";
import { version } from "./version";

export const SetSentryUser = function () {
  const { user } = useAuthentication();

  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
    } else {
      Sentry.setUser({
        email: user.email,
        ip_address: "{{auto}}",
        id: user.id.toString(),
      });
    }
  }, [user]);

  return null;
};

export const SetSentrySession = function () {
  const currentSession = useAppSelector(selectSessionState);

  useEffect(() => {
    if (!currentSession.id) {
      Sentry.setContext("portalSession", null);
    } else {
      Sentry.setContext("portalSession", {
        id: currentSession.id,
        applicationBuildId: currentSession.applicationBuildId,
        deviceIdentifier: currentSession.deviceIdentifier,
      });
    }
  }, [currentSession]);

  return null;
};

export const setupSentry = (router: unknown) => {
  if (import.meta.env.PROD && sentryDSN) {
    Sentry.init({
      dsn: sentryDSN,
      integrations: [
        Sentry.tanstackRouterBrowserTracingIntegration(router),
        Sentry.browserTracingIntegration(),
      ],
      release: `portal-frontend@${version}`,

      // Strip streaming-gateway auth (authorization / routing-key query params)
      // from breadcrumb URLs/messages before they're recorded — the default
      // fetch/XHR breadcrumb integration otherwise captures the full request URL
      // (incl. query string), which would ship the gateway token to Sentry on
      // any later captured event.
      beforeBreadcrumb(breadcrumb) {
        if (typeof breadcrumb.data?.url === "string") {
          breadcrumb.data.url = redactGatewaySecrets(breadcrumb.data.url);
        }
        if (typeof breadcrumb.message === "string") {
          breadcrumb.message = redactGatewaySecrets(breadcrumb.message);
        }
        return breadcrumb;
      },

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0.1,
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications.",
      ],
    });

    // Integrate loglevel with Sentry, automatically pushing errors to Sentry
    const originalLogMethodFactory = log.methodFactory;
    log.methodFactory = function (methodName, logLevel, loggerName) {
      const rawLogggingMethod = originalLogMethodFactory(
        methodName,
        logLevel,
        loggerName,
      );

      return function (message) {
        if (logLevel >= log.levels.ERROR) {
          Sentry.captureException(message);
        }

        rawLogggingMethod(message);
      };
    };
    log.rebuild();
  }
};
